import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  InputBase,
  IconButton,
  CircularProgress,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Divider,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import SendIcon from '@material-ui/icons/Send';
import DoneIcon from '@material-ui/icons/Done';
import AddIcon from '@material-ui/icons/Add';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import GroupIcon from '@material-ui/icons/Group';
import { ChevronLeft } from 'lucide-react';

export default function BroadcastDialog({
  open,
  onClose,
  contacts = [],
  property,
  onSendBroadcast,
  sending = false,
  isSuperkos = false,
  broadcastLists = [], // Array of saved broadcast lists
  onSaveBroadcastList,
  onDeleteBroadcastList,
  onUpdateBroadcastList,
  onSelectBroadcastChat,
}) {
  const [currentView, setCurrentView] = useState('lists'); // 'lists', 'create', 'compose'
  const [broadcastStep, setBroadcastStep] = useState(1);
  const [broadcastText, setBroadcastText] = useState('');
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [searchBroadcast, setSearchBroadcast] = useState('');
  const [selectedBroadcastList, setSelectedBroadcastList] = useState(null);
  const [listName, setListName] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuListId, setMenuListId] = useState(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setCurrentView('lists');
      setBroadcastStep(1);
      setBroadcastText('');
      setSelectedPhones([]);
      setSearchBroadcast('');
      setSelectedBroadcastList(null);
      setListName('');
    }
  }, [open]);

  // Normalize phone number
  const normalizePhone = (phone) => String(phone || '').replace(/[^\d]/g, '');

  // Create phone name map
  const phoneNameMap = useMemo(() => {
    const map = new Map();
    contacts.forEach((contact) => {
      const cleanPhone = normalizePhone(contact.phone);
      if (!cleanPhone) return;
      
      const name = contact.name || 'Unknown';
      const roomName = contact.roomName || '';
      const displayName = roomName ? `${name} (${roomName})` : name;
      
      if (!map.has(cleanPhone)) {
        map.set(cleanPhone, displayName);
      }
    });
    return map;
  }, [contacts]);

  // Get all available phone numbers
  const allPhones = useMemo(() => Array.from(phoneNameMap.keys()), [phoneNameMap]);

  // Filter contacts for search
  const filteredContacts = useMemo(() => {
    if (!searchBroadcast.trim()) return contacts;
    const searchLower = searchBroadcast.toLowerCase();
    return contacts.filter(contact => 
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.roomName?.toLowerCase().includes(searchLower) ||
      contact.phone?.toLowerCase().includes(searchLower)
    );
  }, [contacts, searchBroadcast]);

  // Handle close
  const handleClose = () => {
    setCurrentView('lists');
    setBroadcastStep(1);
    setBroadcastText('');
    setSelectedPhones([]);
    setSearchBroadcast('');
    setSelectedBroadcastList(null);
    setListName('');
    onClose();
  };

  // Handle send broadcast
  const handleSendBroadcast = async () => {
    if (!broadcastText.trim() || !selectedPhones.length) return;
    
    await onSendBroadcast({
      phones: selectedPhones,
      text: broadcastText.trim(),
      listId: selectedBroadcastList?.id,
    });
    
    handleClose();
  };

  // Handle save new broadcast list
  const handleSaveBroadcastList = async () => {
    if (!listName.trim() || !selectedPhones.length) return;
    
    const newList = {
      id: Date.now().toString(),
      name: listName.trim(),
      phones: selectedPhones,
      createdAt: new Date().toISOString(),
      property: property?.name,
    };
    
    await onSaveBroadcastList?.(newList);
    setSelectedBroadcastList(newList);
    setCurrentView('compose');
  };

  // Handle use existing list - redirect to chat instead of compose
  const handleUseExistingList = (list) => {
    // Close the dialog and open the broadcast list as a chat room
    handleClose();
    onSelectBroadcastChat?.(list);
  };

  // Add option to compose new message for existing list
  const handleComposeToExistingList = (list) => {
    setSelectedBroadcastList(list);
    setSelectedPhones(list.phones || []);
    setCurrentView('compose');
  };

  // Handle menu actions
  const handleMenuOpen = (event, listId) => {
    setMenuAnchor(event.currentTarget);
    setMenuListId(listId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuListId(null);
  };

  const handleEditList = () => {
    const list = broadcastLists.find(l => l.id === menuListId);
    if (list) {
      setSelectedBroadcastList(list);
      setSelectedPhones(list.phones || []);
      setListName(list.name || '');
      setCurrentView('create');
      setBroadcastStep(1);
    }
    handleMenuClose();
  };

  const handleDeleteList = async () => {
    await onDeleteBroadcastList?.(menuListId);
    handleMenuClose();
  };

  const formatLastUsed = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hari ini';
    if (diffDays === 1) return 'Kemarin';
    if (diffDays < 7) return `${diffDays} hari yang lalu`;
    return date.toLocaleDateString('id-ID');
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        style: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      {currentView === 'lists' ? (
        // Main View: Broadcast Lists
        <>
          <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Broadcast Lists
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Pilih list yang sudah ada atau buat yang baru
            </Typography>
          </DialogTitle>

          <DialogContent style={{ padding: 0 }}>
            {/* New List Option */}
            <ListItem 
              button 
              onClick={() => {
                setCurrentView('create');
                setBroadcastStep(1);
                setSelectedPhones([]);
                setListName('');
              }}
              style={{ borderBottom: '1px solid #f0f0f0' }}
            >
              <ListItemAvatar>
                <Avatar style={{ backgroundColor: '#25d366', color: 'white' }}>
                  <AddIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography style={{ fontWeight: 600 }}>
                    New List
                  </Typography>
                }
                secondary="Buat broadcast list baru"
              />
            </ListItem>

            {/* Existing Lists */}
            <List style={{ padding: 0 }}>
              {broadcastLists.length === 0 ? (
                <Box p={4} textAlign="center">
                  <Typography variant="body2" color="textSecondary">
                    Belum ada broadcast list yang tersimpan
                  </Typography>
                </Box>
              ) : (
                broadcastLists.map((list) => (
                  <ListItem
                    key={list.id}
                    button
                    onClick={() => handleUseExistingList(list)} // This will now open as chat
                  >
                    <ListItemAvatar>
                      <Avatar style={{ backgroundColor: '#128c7e', color: 'white' }}>
                        <GroupIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography style={{ fontWeight: 600 }}>
                          {list.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {list.phones?.length || 0} kontak
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {list.lastMessage ? list.lastMessage : 'Tap untuk kirim pesan'}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, list.id);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              )}
            </List>
          </DialogContent>

          <DialogActions style={{ padding: '16px 24px' }}>
            <Button onClick={handleClose}>
              Tutup
            </Button>
          </DialogActions>

          {/* Context Menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => {
              const list = broadcastLists.find(l => l.id === menuListId);
              if (list) handleComposeToExistingList(list);
              handleMenuClose();
            }}>
              <SendIcon style={{ marginRight: 8, fontSize: 18 }} />
              Kirim Pesan
            </MenuItem>
            <MenuItem onClick={handleEditList}>
              <EditIcon style={{ marginRight: 8, fontSize: 18 }} />
              Edit List
            </MenuItem>
            <MenuItem onClick={handleDeleteList} style={{ color: '#f44336' }}>
              <DeleteIcon style={{ marginRight: 8, fontSize: 18 }} />
              Hapus List
            </MenuItem>
          </Menu>
        </>
      ) : currentView === 'create' ? (
        // Create/Edit List View
        broadcastStep === 1 ? (
          // Step 1: Select Contacts
          <>
            <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
              <Box display="flex" alignItems="center">
                <IconButton 
                  onClick={() => setCurrentView('lists')} 
                  size="small" 
                  style={{ marginRight: 8 }}
                >
                  <ChevronLeft size={20} />
                </IconButton>
                <Box flex={1}>
                  <Typography variant="h6" style={{ fontWeight: 600 }}>
                    {selectedBroadcastList ? 'Edit List' : 'New Broadcast List'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedPhones.length} dari {allPhones.length} dipilih
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => setSelectedPhones(
                    allPhones.length === selectedPhones.length ? [] : [...allPhones]
                  )}
                  disabled={sending}
                  style={{ textTransform: 'none' }}
                >
                  {allPhones.length === selectedPhones.length ? 'Batalkan Semua' : 'Pilih Semua'}
                </Button>
              </Box>
            </DialogTitle>

            <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Search Bar */}
              <Box p={2} borderBottom="1px solid #f0f0f0">
                <Box display="flex" alignItems="center" bgcolor="#f5f6f6" borderRadius={2} p={1}>
                  <SearchIcon fontSize="small" />
                  <InputBase
                    placeholder="Cari tenant atau nomor..."
                    style={{ marginLeft: 8, width: '100%' }}
                    value={searchBroadcast}
                    onChange={(e) => setSearchBroadcast(e.target.value)}
                  />
                </Box>
              </Box>

              {/* Selected Contacts Preview */}
              {selectedPhones.length > 0 && (
                <Box p={2} bgcolor="#f8f9fa" borderBottom="1px solid #f0f0f0">
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    Kontak Terpilih:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" style={{ gap: 6, maxHeight: 80, overflow: 'auto' }}>
                    {selectedPhones.slice(0, 10).map((phone) => {
                      const name = phoneNameMap.get(phone) || phone;
                      return (
                        <Chip
                          key={phone}
                          size="small"
                          label={name.split(' ')[0]}
                          onDelete={() => setSelectedPhones(prev => prev.filter(p => p !== phone))}
                          style={{ fontSize: 11 }}
                        />
                      );
                    })}
                    {selectedPhones.length > 10 && (
                      <Chip
                        size="small"
                        label={`+${selectedPhones.length - 10} lainnya`}
                        style={{ fontSize: 11, backgroundColor: '#e3f2fd' }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Contacts List */}
              <Box flex={1} overflow="auto">
                {filteredContacts.length === 0 ? (
                  <Box p={4} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchBroadcast ? 'Tidak ada kontak yang ditemukan' : 'Tidak ada kontak tersedia'}
                    </Typography>
                  </Box>
                ) : (
                  <List style={{ padding: 0 }}>
                    {filteredContacts.map((contact) => {
                      const phone = normalizePhone(contact.phone);
                      const isSelected = selectedPhones.includes(phone);
                      
                      return (
                        <ListItem
                          key={contact.id}
                          button
                          onClick={() => {
                            if (sending) return;
                            setSelectedPhones(prev =>
                              isSelected
                                ? prev.filter(p => p !== phone)
                                : [...prev, phone]
                            );
                          }}
                          style={{ 
                            paddingLeft: 16, 
                            paddingRight: 16,
                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar style={{ 
                              backgroundColor: isSelected ? '#1976d2' : '#9e9e9e',
                              color: 'white',
                              width: 40,
                              height: 40
                            }}>
                              {isSelected ? (
                                <DoneIcon />
                              ) : (
                                (contact.name || '??').slice(0, 2).toUpperCase()
                              )}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography style={{ fontWeight: isSelected ? 600 : 400 }}>
                                {contact.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" color="textSecondary">
                                {contact.phone} • {contact.roomName}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            </DialogContent>

            <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
              <Button onClick={() => setCurrentView('lists')}>
                Batal
              </Button>
              <Button
                onClick={() => setBroadcastStep(2)}
                color="primary"
                variant="contained"
                disabled={selectedPhones.length === 0}
                style={{ borderRadius: 20, paddingLeft: 24, paddingRight: 24 }}
              >
                Lanjut ({selectedPhones.length})
              </Button>
            </DialogActions>
          </>
        ) : (
          // Step 2: Name the List
          <>
            <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
              <Box display="flex" alignItems="center">
                <IconButton 
                  onClick={() => setBroadcastStep(1)} 
                  size="small" 
                  style={{ marginRight: 8 }}
                >
                  <ChevronLeft size={20} />
                </IconButton>
                <Box flex={1}>
                  <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Nama Broadcast List
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedPhones.length} kontak dipilih
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent style={{ padding: '16px 24px' }}>
              <TextField
                label="Nama List"
                placeholder="Masukkan nama untuk broadcast list..."
                variant="outlined"
                fullWidth
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                autoFocus
                style={{ marginBottom: 16 }}
              />

              <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                Contoh: &quot;Tenant Lantai 1&quot;, &quot;Semua Tenant&quot;, &quot;VIP Customers&quot;
              </Typography>
            </DialogContent>

            <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
              <Button onClick={() => setBroadcastStep(1)}>
                Kembali
              </Button>
              <Button
                onClick={handleSaveBroadcastList}
                color="primary"
                variant="contained"
                disabled={!listName.trim()}
                style={{ borderRadius: 20, paddingLeft: 24, paddingRight: 24 }}
              >
                Simpan & Lanjut
              </Button>
            </DialogActions>
          </>
        )
      ) : (
        // Compose Message View
        <>
          <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
            <Box display="flex" alignItems="center">
              <IconButton 
                onClick={() => setCurrentView('lists')} 
                size="small" 
                style={{ marginRight: 8 }}
              >
                <ChevronLeft size={20} />
              </IconButton>
              <Box flex={1}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {selectedBroadcastList?.name || 'Broadcast Message'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedPhones.length} kontak • {property?.name || '-'}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column' }}>
            {/* Selected contacts summary */}
            <Box mb={2} p={2} bgcolor="#f8f9fa" borderRadius={2}>
              <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                Penerima Broadcast:
              </Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                {selectedPhones.slice(0, 5).map((phone) => {
                  const name = phoneNameMap.get(phone) || phone;
                  return (
                    <Chip
                      key={phone}
                      size="small"
                      label={name.split(' ')[0]}
                      style={{ fontSize: 10, height: 20 }}
                    />
                  );
                })}
                {selectedPhones.length > 5 && (
                  <Typography variant="caption" color="textSecondary" style={{ alignSelf: 'center' }}>
                    dan {selectedPhones.length - 5} kontak lainnya
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Message input */}
            <TextField
              placeholder="Ketik pesan broadcast..."
              variant="outlined"
              fullWidth
              multiline
              rows={8}
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
              disabled={sending}
              InputProps={{
                style: {
                  fontSize: 14,
                  lineHeight: 1.5
                }
              }}
              style={{ marginBottom: 16 }}
            />

            <Typography variant="caption" color="textSecondary">
              {broadcastText.length} karakter
            </Typography>
          </DialogContent>

          <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setCurrentView('lists')}>
              Kembali
            </Button>
            <Button
              onClick={handleSendBroadcast}
              color="primary"
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={sending || !broadcastText.trim() || isSuperkos}
              style={{ 
                borderRadius: 20, 
                paddingLeft: 24, 
                paddingRight: 24,
                backgroundColor: sending ? '#ccc' : '#25d366',
                '&:hover': {
                  backgroundColor: sending ? '#ccc' : '#1ea952'
                }
              }}
            >
              {sending ? 'Mengirim...' : 'Kirim Broadcast'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}